import React from 'react';
import {Draggable} from 'react-beautiful-dnd';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../../incase-domain/components/button/Button';
import {IElementSequenceQuestion} from '../../../../../incase-domain/elements/question/questionSequence';
import {MAX_ANSWER_LENGTH} from '../../../../../incase-domain/validators/elements/constant';
import {answerValidator} from '../../../../../incase-domain/validators/elements/questionBase';
import {getErrorClasses} from '../../../../../utils/validation';
import DebouncedTextInput from '../../common/debounced/DebouncedTextInput';
import SequenceChangeComment from './SequenceChangeComment';

interface SequenceQuestionAnswerProps {
    answer: string;
    validationErrors: IValidationErrorObject | null;
    answerChangeHandler?: (index: number, value: string) => void;
    inactive?: boolean;
    index: number;
    correctSequenceIndex: number;
    removeAnswer: (index: number) => void;
    highlightedElement: IElementSequenceQuestion;
    highlightedElementChangeHandler: {
        single: (field: keyof IElementSequenceQuestion | string, value: unknown) => void;
    };
}

function SequenceQuestionAnswer({
    answer,
    answerChangeHandler,
    index,
    correctSequenceIndex,
    removeAnswer,
    validationErrors,
    inactive,
    highlightedElement,
    highlightedElementChangeHandler,
}: SequenceQuestionAnswerProps): JSX.Element {
    return (
        <Draggable draggableId={`${correctSequenceIndex}-${answer}`} index={correctSequenceIndex}>
            {(provided) => {
                return (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`l-group__item ${getErrorClasses(
                            validationErrors,
                            `sequence.${index}`
                        )} c-sequenceQuestionOrder__container`}
                        data-cy="change-answer"
                    >
                        <DebouncedTextInput
                            className="c-sidepanel__input"
                            value={answer}
                            onChange={(e: string) => {
                                if (answerChangeHandler && !inactive) {
                                    answerChangeHandler(index, e);
                                }
                            }}
                            validator={(val) => {
                                const validation = answerValidator.shape.text.safeParse(val);
                                return validation.success;
                            }}
                            maxLength={MAX_ANSWER_LENGTH}
                            key={index}
                            disabled={!answerChangeHandler}
                        />

                        <div {...provided.dragHandleProps}>
                            <Button
                                variant="ghost"
                                disableHover
                                className="c-sequenceQuestionOrder"
                                padded={false}
                                trailing="material-symbols:drag-indicator"
                            />
                        </div>

                        <div style={{minHeight: '28px'}} className="u-flex u-h-center u-h-between">
                            <div className="c-newAnswer__iconContainer">
                                <SequenceChangeComment
                                    index={index}
                                    highlightedElement={highlightedElement}
                                    highlightedElementChangeHandler={highlightedElementChangeHandler}
                                />

                                <Button
                                    variant="ghost"
                                    color="error"
                                    square
                                    size="sm"
                                    leading="solar:trash-bin-minimalistic-linear"
                                    key={`${index}-delete`}
                                    onClick={() => removeAnswer(index)}
                                    disabled={highlightedElement.sequence.length < 2}
                                    data-cy="removeAnswer"
                                />
                            </div>
                        </div>
                    </div>
                );
            }}
        </Draggable>
    );
}

export default SequenceQuestionAnswer;
